import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Button, Grid, Paper, TableContainer, Tooltip } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router';
import * as api from '../../api/app/shiftsApi';
import { AlertDialog } from '../../components/core/AlertDialog';
import { ApiError } from '../../components/core/ApiError/ApiError';
import DataGridTable from '../../components/core/DataGridTable/DataGridTable';
import { Loader } from '../../components/core/Loader/Loader';
import { useTranslation } from '../../contexts/translation';
import { useDeleteDialog } from '../../hooks/useDeleteDialog';
import { Shift } from '../../types/app/shifts.types';
import { paths } from '../../utils/core/routes';
interface Props {
  isReadOnly: boolean;
}
export function ShiftList({ isReadOnly }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const {
    isOpen: isDeleteDialogOpen,
    idToDelete,
    setDeleteAttempt,
    cancelDeleteAttempt,
    confirmDeleteAttempt,
  } = useDeleteDialog();

  const { data, isLoading, isError } = useQuery<Shift[]>('shifts', api.getShiftList);
  const { mutate: mutateDeleteShift } = useMutation(api.deleteShift);

  const columns = [
    { headerName: t('page.shift.table-col-1'), field: 'name', width: 200 },
    {
      headerName: t('page.shift.table-col-2'),
      field: 'start',
      width: 200,
    },
    {
      headerName: t('page.shift.table-col-3'),
      field: 'end',
      width: 200,
    },
    {
      field: '',
      sortable: false,
      disableColumnMenu: true,
      hide: isReadOnly,
      minWidth: 150,
      flex: 1,
      align: 'right',
      renderCell: (params: any) => {
        return (
          <>
            {!isReadOnly && (
              <>
                <Tooltip title={t('general.action-buttons.edit')}>
                  <Button onClick={() => handleOnEdit(params.id)}>
                    <EditIcon />
                  </Button>
                </Tooltip>
                <Tooltip title={t('general.action-buttons.delete')}>
                  <Button onClick={() => handleOnDelete(params.id)}>
                    <DeleteIcon />
                  </Button>
                </Tooltip>
              </>
            )}
          </>
        );
      },
    },
  ];

  const handleOnCreate = () => navigate(paths.SHIFTS_CREATE);
  const handleOnEdit = (id: number) => navigate(`${paths.SHIFTS_DETAIL}/${id}`);
  const handleOnDelete = (id: number) => setDeleteAttempt(id);

  const handleConfirm = () => {
    if (!idToDelete) return;

    mutateDeleteShift(idToDelete, {
      onSuccess: () => {
        enqueueSnackbar(t('page.shift.item-deleted'), { variant: 'success' });
        queryClient.invalidateQueries(['shifts']);
        confirmDeleteAttempt();
      },
      onError: (result: any) => {
        enqueueSnackbar(`${result}`, { variant: 'error' });
      },
    });
  };

  if (isError) return <ApiError />;
  if (isLoading || !data) return <Loader />;
  return (
    <>
      <Grid item xs={8}>
        <TableContainer component={Paper}>
          <div style={{ height: '70vh', width: '100%' }}>
            <DataGridTable
              height={'70vh'}
              data={data}
              columns={columns}
              fulltextSearch={true}
              autoPageSize={true}
            />
          </div>
          {!isReadOnly && (
            <Tooltip title={t('general.action-buttons.new')}>
              <Button onClick={handleOnCreate}>
                <AddIcon />
              </Button>
            </Tooltip>
          )}
        </TableContainer>
      </Grid>
      <AlertDialog
        open={isDeleteDialogOpen}
        title={t('general.action-buttons.delete')}
        content={t('general.messages.warning.delete-message')}
        handleClose={cancelDeleteAttempt}
        handleConfirm={handleConfirm}
      />
    </>
  );
}
